import {
  IconButton,
  Tooltip
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import {
  ReactNode,
  useState
} from "react"
import { useTheme } from "../providers/theme"
import {
  ExpandMoreRounded,
  ExpandLessRounded
} from '@mui/icons-material'

export function PropertyArea({
  property,
  content,
  image,
  actionButton,
  actionButtonsList,
  classes
}: {
  property: string,
  content: string,
  image?: string,
  actionButton?: boolean,
  actionButtonsList?: {
    tooltip: string,
    disabled: boolean,
    onClick: () => void,
    icon: ReactNode,
    loading?: any
  }[],
  classes: any
}) {
  return (
    <div
      className={classes.container}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%"
        }}
      >
        {
          classes.property &&
            <p
              className={classes.property}
            >
              {property}
            </p>
        }
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".5em",
            width: "100%",
            marginBottom: `${image ? ".25em" : "0em"}`
          }}
        >
          {
            image &&
              <img
                src={image}
                alt="Embosser"
                style={{
                  maxHeight: "40px",
                  borderRadius: "50%",
                  aspectRatio: "1",
                  objectFit: "cover"
                }}
              >
              </img>
          }
          <p
            className={classes.content}
          >
            {content}
          </p>
        </div>
      </div>
      {
        actionButton &&
          actionButtonsList!.map((actionButtonsIterator) =>
            !actionButtonsIterator.disabled ?
              <Tooltip
                title={`${actionButtonsIterator.tooltip}`}
                key={actionButtonsIterator.tooltip}
              >
                {
                  actionButtonsIterator.loading ?
                    <LoadingButton
                      disabled={actionButtonsIterator.disabled}
                      sx={{
                        fontSize: "1.25em !important",
                        maxHeight: "50px !important"
                      }}
                      onClick={actionButtonsIterator.onClick}
                      loading={actionButtonsIterator.loading}
                    >
                      {actionButtonsIterator.icon}
                    </LoadingButton>
                  :
                    <IconButton
                      disabled={actionButtonsIterator.disabled}
                      sx={{
                        fontSize: "1.25em !important",
                        maxHeight: "50px !important"
                      }}
                      onClick={actionButtonsIterator.onClick}
                    >
                      {actionButtonsIterator.icon}
                    </IconButton>
                }
              </Tooltip>
            :
              <IconButton
                key={actionButtonsIterator.tooltip}
                disabled={actionButtonsIterator.disabled}
                sx={{
                  fontSize: "1.25em !important",
                  maxHeight: "50px !important"
                }}
                onClick={actionButtonsIterator.onClick}
              >
                {actionButtonsIterator.icon}
              </IconButton>
          )
      }
    </div>
  )
}

export function MultiplePropertiesArea({
  property,
  topMargin,
  horizontalMargin,
  marginLeft,
  paddingInline,
  paddingBottom,
  actionButton,
  actionButtonsFocusDisabled,
  actionButtonsList,
  classes,
  children
}: {
  property: string,
  topMargin?: boolean,
  horizontalMargin?: boolean,
  marginLeft?: boolean,
  paddingInline?: boolean,
  paddingBottom?: boolean
  actionButton?: boolean,
  actionButtonsFocusDisabled?: boolean,
  actionButtonsList?: {
    tooltip: string,
    disabled: boolean,
    onClick: () => void,
    icon: ReactNode,
    loading?: any
  }[],
  classes: any
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const {mode} = useTheme()

  return (
    <div 
      className={`
        ${open ? classes.propertyAreaContainerOpen : classes.propertyAreaContainerClosed}
        ${topMargin && classes.propertyAreaTopMargin}
        ${horizontalMargin && classes.propertyAreaMarginHorizontal}
        ${marginLeft && classes.propertyAreaMarginLeft}
        ${paddingInline && classes.propertyAreaPaddingInline}
        ${open && paddingBottom && classes.propertyAreaPaddingBottom}
      `}
    >
      <div className={classes.propertyAreaContainerHeader}>
        <p
          className={classes.propertyAreaProperty}
          style={{
            color: mode === 'light' ? '#252525' : 'white',
            width: "100%",
            textAlign: "left"
          }}
        >
          {property}
        </p>
        {
          actionButton &&
            actionButtonsList!.map((actionButtonsIterator) =>
              !actionButtonsIterator.disabled ?
                <Tooltip
                  title={`${actionButtonsIterator.tooltip}`}
                  key={actionButtonsIterator.tooltip}
                >
                  {
                    actionButtonsIterator.loading ?
                      <LoadingButton
                        disabled={actionButtonsIterator.disabled}
                        tabIndex={!!actionButtonsFocusDisabled ? -1 : 0}
                        aria-hidden={!!actionButtonsFocusDisabled}
                        aria-labelledby={actionButtonsIterator.tooltip}
                        aria-describedby={actionButtonsIterator.tooltip}
                        sx={{
                          fontSize: "1.25em !important",
                          maxHeight: "50px !important"
                        }}
                        onClick={actionButtonsIterator.onClick}
                        loading={actionButtonsIterator.loading}
                      >
                        {actionButtonsIterator.icon}
                      </LoadingButton>
                    :
                      <IconButton
                        disabled={actionButtonsIterator.disabled}
                        tabIndex={!!actionButtonsFocusDisabled ? -1 : 0}
                        aria-hidden={!!actionButtonsFocusDisabled}
                        aria-labelledby={actionButtonsIterator.tooltip}
                        aria-describedby={actionButtonsIterator.tooltip}
                        sx={{
                          fontSize: "1.25em !important",
                          maxHeight: "50px !important"
                        }}
                        onClick={actionButtonsIterator.onClick}
                      >
                        {actionButtonsIterator.icon}
                      </IconButton>
                  }
                </Tooltip>
              :
                <IconButton
                  key={actionButtonsIterator.tooltip}
                  tabIndex={actionButtonsFocusDisabled ? -1 : 0}
                  aria-hidden={!!actionButtonsFocusDisabled}
                  aria-labelledby={actionButtonsIterator.tooltip}
                  aria-describedby={actionButtonsIterator.tooltip}
                  disabled={actionButtonsIterator.disabled}
                  sx={{
                    fontSize: "1.25em !important",
                    maxHeight: "50px !important"
                  }}
                  onClick={actionButtonsIterator.onClick}
                >
                  {actionButtonsIterator.icon}
                </IconButton>
            )
        }
        <Tooltip
          title={`Expand ${property}`}
          key={`Expand ${property}`}
        >
          <IconButton
            sx={{
              height: "auto !important"
            }}
            tabIndex={!!actionButtonsFocusDisabled ? -1 : 0}
            aria-hidden={!!actionButtonsFocusDisabled}
            aria-labelledby={`expand ${property}`}
            onClick={() => {
              setOpen(!open)
            }}
          >
            {
              open ? 
                <ExpandLessRounded />
              :
                <ExpandMoreRounded />
            }
          </IconButton>
        </Tooltip>
      </div>
      {
        open ?
          children
        :
          null
      }
    </div>
  )
}